import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { COMPACT_DIALOG_PAPER_SX } from '@/components/shared/dialogStyles';
import {
  getExportFormats,
  MAX_EXTRACT_RECORDS,
  type SupplyPointTab,
} from '@/services/supplyPoint/supplyPointService';

const DIALOG_TITLE_SX = {
  p: 0,
  color: 'rgba(0, 0, 0, 0.87)',
  fontFamily: 'Roboto, sans-serif',
  fontSize: '20px',
  fontWeight: 500,
  letterSpacing: '.25px',
  lineHeight: '26px',
};

const DIALOG_MESSAGE_SX = {
  color: 'rgba(0, 0, 0, 0.6)',
  fontFamily: 'Roboto, sans-serif',
  fontSize: '16px',
  letterSpacing: '.5px',
  lineHeight: '28px',
  mb: 3,
};

const CANCEL_BUTTON_SX = {
  maxHeight: '36px',
  px: '12px',
  color: '#0c479d !important',
  backgroundColor: '#fff !important',
  boxShadow: 'none !important',
  fontWeight: 500,
  letterSpacing: '.0892857143em',
  textTransform: 'none',
};

const PRIMARY_BUTTON_SX = {
  maxHeight: '36px',
  letterSpacing: '0.08em',
  color: 'common.white',
  backgroundColor: '#0c479d !important',
  '&.Mui-disabled': { backgroundColor: '#e0e0e0 !important', color: '#9e9e9e !important' },
};

const compactPaper = { paper: { sx: COMPACT_DIALOG_PAPER_SX } };

interface SupplyPointExportDialogProps {
  open: boolean;
  tab: SupplyPointTab;
  onClose: () => void;
  onDownload: (format: string) => void;
}

export const SupplyPointExportDialog: React.FC<SupplyPointExportDialogProps> = ({ open, tab, onClose, onDownload }) => {
  const formats = getExportFormats(tab);
  const [format, setFormat] = useState(formats[0]?.format ?? '');

  useEffect(() => {
    if (open) setFormat(getExportFormats(tab)[0]?.format ?? '');
  }, [open, tab]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} slotProps={compactPaper}>
      <Box sx={{ m: 3 }}>
        <DialogTitle id="qa-title-lbl" sx={DIALOG_TITLE_SX}>
          {`Export ${MAX_EXTRACT_RECORDS} entries for ${tab.toLowerCase()} supply point.`}
        </DialogTitle>
        <DialogContent sx={{ px: 0, py: 0, mt: 3 }}>
          <Typography id="qa-message-lbl" sx={DIALOG_MESSAGE_SX}>
            Exports are limited to {MAX_EXTRACT_RECORDS} entries. Search and Filters will affect which entries are
            exported. Please select the format for this export.
          </Typography>
          <TextField
            select
            fullWidth
            id="qa-templateFormat-sel"
            label="Template Format"
            value={format}
            onChange={(event) => setFormat(event.target.value)}
          >
            {formats.map((item) => (
              <MenuItem key={item.format} value={item.format}>{item.label}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ m: 0, mt: 3, p: '0 !important', gap: '12px', justifyContent: 'flex-start' }}>
          <Button id="qa-cancel-btn" onClick={onClose} sx={CANCEL_BUTTON_SX}>CANCEL</Button>
          <Button
            id="qa-downloadSupplyPoint-btn"
            variant="contained"
            disableElevation
            disabled={!format}
            onClick={() => onDownload(format)}
            sx={PRIMARY_BUTTON_SX}
          >
            DOWNLOAD
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
